import type { AdvancedFilterQuery } from '@packages/types';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ActiveFilter } from '../types/filter-metadata.types';
import type { FilterCondition, FilterGroup, GroupOperator } from '../types/filter-panel.types';

interface UseFilterGroupsProps {
    initialAdvanced?: AdvancedFilterQuery;
    initialFilters?: ActiveFilter[];
    setFieldLabels: (
        updater:
            | Record<string, Record<string, string>>
            | ((prev: Record<string, Record<string, string>>) => Record<string, Record<string, string>>)
    ) => void;
    setMultiSelectInValues: (
        updater: Record<string, any[]> | ((prev: Record<string, any[]>) => Record<string, any[]>)
    ) => void;
    setMultiSelectOutValues: (
        updater: Record<string, any[]> | ((prev: Record<string, any[]>) => Record<string, any[]>)
    ) => void;
}

interface UseFilterGroupsReturn {
    filterGroups: FilterGroup[];
    setFilterGroups: React.Dispatch<React.SetStateAction<FilterGroup[]>>;
    showGroupOperatorSelection: boolean;
    setShowGroupOperatorSelection: React.Dispatch<React.SetStateAction<boolean>>;
    containerRef: React.RefObject<HTMLDivElement | null>;
    lastAddedGroupRef: React.MutableRefObject<string | null>;
    lastAddedConditionRef: React.MutableRefObject<{ groupId: string; conditionId: string } | null>;
    updateCondition: (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => void;
    clearFilters: (onFiltersChange?: (filters: ActiveFilter[]) => void) => void;
    addFilterGroup: (groupOperator?: GroupOperator) => void;
    removeFilterGroup: (groupId: string) => void;
    toggleGroupOperator: (groupId: string) => void;
    addCondition: (groupId: string, conditionOperator?: GroupOperator) => void;
    removeCondition: (groupId: string, conditionId: string) => void;
}

/**
 * Converts initialAdvanced or initialFilters to FilterGroup[] format
 */
const createInitialGroups = (initialAdvanced?: AdvancedFilterQuery, initialFilters?: ActiveFilter[]): FilterGroup[] => {
    if (initialAdvanced?.groups?.length) {
        return initialAdvanced.groups.map((group, idx) => ({
            id: `${idx + 1}`,
            operator: (group.operator || 'AND') as GroupOperator,
            conditions: group.conditions.map((c, cIdx) => ({
                id: `${idx + 1}-${cIdx + 1}`,
                field: c.field,
                operator: c.operator as FilterCondition['operator'],
                value: c.value,
                conditionOperator: (c as any).conditionOperator as GroupOperator | undefined,
            })),
        }));
    }

    if (initialFilters?.length) {
        const conditions: FilterCondition[] = initialFilters.map((f, idx) => ({
            id: `${Date.now()}-${idx}`,
            field: f.field,
            operator: f.operator as FilterCondition['operator'],
            value: f.value,
        }));
        return [{ id: '1', operator: 'AND', conditions }];
    }

    // Default: return one group with one condition
    return [
        {
            id: '1',
            operator: 'AND',
            conditions: [{ id: '1-1', field: 'search' }],
        },
    ];
};

/**
 * Hook for managing filter groups state and operations
 */
export const useFilterGroups = ({
    initialAdvanced,
    initialFilters,
    setFieldLabels,
    setMultiSelectInValues,
    setMultiSelectOutValues,
}: UseFilterGroupsProps): UseFilterGroupsReturn => {
    const initialGroups = useMemo(
        () => createInitialGroups(initialAdvanced, initialFilters),
        [initialAdvanced, initialFilters]
    );

    const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(initialGroups);
    const [showGroupOperatorSelection, setShowGroupOperatorSelection] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const lastAddedGroupRef = useRef<string | null>(null);
    const lastAddedConditionRef = useRef<{ groupId: string; conditionId: string } | null>(null);

    /**
     * Cleans up multi-select state for a specific condition
     */
    const cleanupConditionState = useCallback(
        (conditionKey: string) => {
            setFieldLabels(prev => {
                const newState = { ...prev };
                delete newState[conditionKey];
                return newState;
            });
            setMultiSelectInValues(prev => {
                const newState = { ...prev };
                delete newState[conditionKey];
                return newState;
            });
            setMultiSelectOutValues(prev => {
                const newState = { ...prev };
                delete newState[conditionKey];
                return newState;
            });
        },
        [setFieldLabels, setMultiSelectInValues, setMultiSelectOutValues]
    );

    const updateCondition = useCallback(
        (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => {
            // If field is changing, clean up multi-select values for this condition
            if (updates.field !== undefined) {
                cleanupConditionState(`${groupId}-${conditionId}`);
            }
            setFilterGroups(groups =>
                groups.map(group =>
                    group.id === groupId
                        ? {
                              ...group,
                              conditions: group.conditions.map(c => (c.id === conditionId ? { ...c, ...updates } : c)),
                          }
                        : group
                )
            );
        },
        [cleanupConditionState]
    );

    const clearFilters = useCallback((onFiltersChange?: (filters: ActiveFilter[]) => void) => {
        setFilterGroups([
            {
                id: `${Date.now()}`,
                operator: 'AND',
                conditions: [{ id: `${Date.now()}-1`, field: 'search' }],
            },
        ]);
        setShowGroupOperatorSelection(false);
        onFiltersChange?.([]);
    }, []);

    const addFilterGroup = useCallback((groupOperator?: GroupOperator) => {
        const newGroupId = `${Date.now()}`;
        lastAddedGroupRef.current = newGroupId;
        setFilterGroups(groups => [
            ...groups,
            {
                id: newGroupId,
                operator: groupOperator || 'AND',
                conditions: [],
            },
        ]);
        setShowGroupOperatorSelection(false);
    }, []);

    const removeFilterGroup = useCallback((groupId: string) => {
        setFilterGroups(groups => groups.filter(g => g.id !== groupId));
    }, []);

    const toggleGroupOperator = useCallback((groupId: string) => {
        setFilterGroups(groups =>
            groups.map(g => (g.id === groupId ? { ...g, operator: g.operator === 'AND' ? 'OR' : 'AND' } : g))
        );
    }, []);

    const addCondition = useCallback((groupId: string, conditionOperator?: GroupOperator) => {
        const newConditionId = `${Date.now()}`;
        lastAddedConditionRef.current = { groupId, conditionId: newConditionId };
        setFilterGroups(groups => {
            const group = groups.find(g => g.id === groupId);
            const isFirstCondition = !group || group.conditions.length === 0;

            return groups.map(g =>
                g.id === groupId
                    ? {
                          ...g,
                          conditions: [
                              ...g.conditions,
                              {
                                  id: newConditionId,
                                  field: 'search',
                                  ...(isFirstCondition ? {} : { conditionOperator: conditionOperator || 'AND' }),
                              },
                          ],
                      }
                    : g
            );
        });
    }, []);

    const removeCondition = useCallback(
        (groupId: string, conditionId: string) => {
            cleanupConditionState(`${groupId}-${conditionId}`);
            setFilterGroups(groups =>
                groups.map(g =>
                    g.id === groupId ? { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) } : g
                )
            );
        },
        [cleanupConditionState]
    );

    return {
        filterGroups,
        setFilterGroups,
        showGroupOperatorSelection,
        setShowGroupOperatorSelection,
        containerRef,
        lastAddedGroupRef,
        lastAddedConditionRef,
        updateCondition,
        clearFilters,
        addFilterGroup,
        removeFilterGroup,
        toggleGroupOperator,
        addCondition,
        removeCondition,
    };
};
